import { bool, string, object, func } from 'prop-types';
import React, { useState, useEffect } from 'react';
import { QueryRenderer, graphql } from 'react-relay';
import { connect } from 'react-redux';
import classnames from 'classnames';

import { trackEvent } from 'dibs-tracking';
import Close from 'dibs-icons/exports/Close';
import MessageBannerCarousel from './MessageBannerCarousel';
import {
    isPreview,
    getPreview,
    getIsFirstTimeVisitor,
    getPreviewVariables,
    isMessageBannerClosedManually,
    setIsBannerClosedManuallyInStorage,
    getTrackingObj,
    hasAnyMessagesByDevice,
} from './messageHelpers';

import styles from './MessageBanner-style.scss';

/**
 * Messages for marketing banner can come from multiple sources and have the following precedence:
 * (1) Preview messages that pulled from a stringfied object on a URL query param
 * (2) Preview messages that are fetched with userType and/or setId from the URL
 * (3) Registered user messages that are fetched with userId
 * (4) Unregistered user messages that are fetched if user is not logged in
 */
function MessageBanner(props) {
    const [variables, setVariables] = useState({
        fireMessageBannerRefetch: false,
        isFirstTimeVisitor: false,
        hasRegisteredUserMessages: false,
        hasUnregisteredUserMessages: false,
        hasPreviewVariables: false,
        previewSetId: '',
        previewUserType: '',
    });
    const [previewMessagesFromUrl, setPreviewMessagesFromUrl] = useState(null);
    const [isBannerClosedManually, setIsBannerClosedManually] = useState(false);
    const [currentMessageTitle, setCurrentMessageTitle] = useState('');
    useEffect(() => {
        const { messages: previewMessages } = getPreview();
        if (previewMessages) {
            setPreviewMessagesFromUrl(previewMessages);
        }
    }, []);
    const {
        isClient,
        hasRootPendingRefetch,
        userId,
        hasUserId,
        isUserTypeTrade,
        useGlobalFallbackMessage,
        relayEnvironment,
        onBannerClose,
    } = props;
    useEffect(() => {
        if (
            !variables.fireMessageBannerRefetch &&
            isClient &&
            !previewMessagesFromUrl &&
            !hasRootPendingRefetch
        ) {
            let nextVariables = {
                ...variables,
                userId,
                fireMessageBannerRefetch: true,
                isFirstTimeVisitor: !hasUserId && getIsFirstTimeVisitor(),
                hasRegisteredUserMessages: hasUserId,
                hasUnregisteredUserMessages: !hasUserId,
                hasPreviewVariables: false,
                previewSetId: '',
                previewUserType: '',
            };

            const { userType: previewUserType, setId: previewSetId } = getPreviewVariables();

            if (previewUserType && previewSetId) {
                nextVariables = {
                    ...nextVariables,
                    hasPreviewVariables: true,
                    previewSetId,
                    previewUserType,
                };
            }
            setVariables(nextVariables);
        }
    }, [hasRootPendingRefetch, isClient, variables, previewMessagesFromUrl, userId, hasUserId]);

    useEffect(() => {
        if (isClient) {
            setIsBannerClosedManually(isMessageBannerClosedManually());
        }
    }, [isClient]);

    const classes = classnames({
        [styles.bannerContainer]: true,
        [styles.isTrade]: isUserTypeTrade,
    });

    if (
        !isClient ||
        isBannerClosedManually ||
        (!variables.fireMessageBannerRefetch && !useGlobalFallbackMessage)
    ) {
        return null;
    }

    if (!variables.fireMessageBannerRefetch && !previewMessagesFromUrl) {
        return <div className={classes} />;
    }

    if (previewMessagesFromUrl) {
        return (
            <div className={classes}>
                <MessageBannerCarousel messages={previewMessagesFromUrl} showAllMessages />
            </div>
        );
    }

    return (
        <QueryRenderer
            environment={relayEnvironment}
            variables={variables}
            query={graphql`
                query MessageBannerQuery(
                    $userId: String!
                    $hasRegisteredUserMessages: Boolean!
                    $hasUnregisteredUserMessages: Boolean!
                    $isFirstTimeVisitor: Boolean!
                    $hasPreviewVariables: Boolean!
                    $previewSetId: String!
                    $previewUserType: String!
                ) {
                    viewer {
                        ...MessageBannerCarousel_viewer
                        user(userId: $userId) @include(if: $hasRegisteredUserMessages) {
                            ...MessageBannerCarousel_user
                            ...messageHelpers_user
                            registeredUserMessages: bannerMessages {
                                ...MessageBannerCarousel_messages
                                ...messageHelpers_messages
                            }
                        }
                        unregisteredUserMessages: messageSet(
                            isFirstTimeVisitor: $isFirstTimeVisitor
                        ) @include(if: $hasUnregisteredUserMessages) {
                            ...MessageBannerCarousel_messages
                            ...messageHelpers_messages
                        }
                        previewMessages: messageSet(
                            setId: $previewSetId
                            userType: $previewUserType
                        ) @include(if: $hasPreviewVariables) {
                            ...MessageBannerCarousel_messages
                            ...messageHelpers_messages
                        }
                    }
                }
            `}
            render={({ error, props: renderProps }) => {
                if (error) {
                    return null;
                } else if (!renderProps) {
                    return useGlobalFallbackMessage ? <div className={classes} /> : null;
                }

                const { viewer } = renderProps;
                const { user } = viewer;
                let messages = viewer?.previewMessages || null;

                if (!isPreview() && user?.registeredUserMessages) {
                    messages = user.registeredUserMessages;
                }
                if (!isPreview() && viewer?.unregisteredUserMessages) {
                    messages = viewer.unregisteredUserMessages;
                }

                if (!useGlobalFallbackMessage && !hasAnyMessagesByDevice(messages, user)) {
                    return null;
                }

                const closeIconClasses = classnames(styles.closeIcon, {
                    [styles.isTrade]: isUserTypeTrade,
                });

                return (
                    <div className={classes} data-tn="marketing-banner">
                        <MessageBannerCarousel
                            viewer={viewer}
                            user={user || null}
                            messages={messages}
                            showAllMessages={variables.hasPreviewVariables}
                            onMessageDisplay={({ messageTitle }) =>
                                setCurrentMessageTitle(messageTitle)
                            }
                        />
                        {!variables.hasPreviewVariables && (
                            <div
                                className={styles.closeIconWrapper}
                                onClick={() => {
                                    setIsBannerClosedManuallyInStorage();
                                    setIsBannerClosedManually(true);
                                    onBannerClose();

                                    trackEvent(
                                        getTrackingObj({
                                            action: 'marketing banner closed',
                                            label: currentMessageTitle,
                                        })
                                    );
                                }}
                            >
                                <Close className={closeIconClasses} />
                            </div>
                        )}
                    </div>
                );
            }}
        />
    );
}

MessageBanner.defaultProps = {
    useGlobalFallbackMessage: false,
    onBannerClose: () => {},
};

MessageBanner.propTypes = {
    hasRootPendingRefetch: bool,
    hasUserId: bool,
    isClient: bool,
    isUserTypeTrade: bool,
    useGlobalFallbackMessage: bool,
    userId: string,
    relayEnvironment: object,
    onBannerClose: func,
};

const mapStateToProps = ({ header }) => {
    const { isUserTypeTrade } = header;

    return {
        isUserTypeTrade,
    };
};

export default connect(mapStateToProps)(MessageBanner);
